import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeedance30I2vParams {
  /**
   * 内容数组，必填参数。使用OpenAI协议格式，包含text和image_url类型的对象
   */
  content: Array<{
    /**
     * 内容类型
     * @values text | image_url
     */
    type: 'text' | 'image_url';
    /**
     * 文本内容（当type为text时）
     */
    text?: string;
    /**
     * 图像URL对象（当type为image_url时）
     */
    image_url?: {
      /**
       * 图像URL或Base64数据URI
       */
      url?: string;
    };
    /**
     * 角色类型，reference_image表示参考图像（可选，1-4张）
     * @values reference_image
     */
    role?: 'reference_image';
  }>;
  /**
   * 模型ID，可选参数。默认为ep-20250507154154-ss6wk
   */
  model?: string;
  /**
   * 分辨率，可选参数。作为文本命令附加到prompt中（--rs）
   * @values 480p | 720p | 1080p
   */
  resolution?: '480p' | '720p' | '1080p';
  /**
   * 宽高比，可选参数。作为文本命令附加到prompt中（--rt）
   * @values 21:9 | 16:9 | 4:3 | 1:1 | 3:4 | 9:16 | 9:21 | keep_ratio | adaptive
   */
  ratio?: '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '9:21' | 'keep_ratio' | 'adaptive';
  /**
   * 视频时长，可选参数。范围3-12秒，作为文本命令附加到prompt中（--dur）
   * @range 3 - 12
   */
  duration?: number;
  /**
   * 帧率，可选参数。支持16或24fps，作为文本命令附加到prompt中（--fps）
   * @values 16 | 24
   */
  framespersecond?: 16 | 24;
  /**
   * 随机种子，可选参数。范围-1到2^32-1，-1表示随机
   * @range -1 - 4294967295
   */
  seed?: number;
  /**
   * 是否添加水印，可选参数。作为文本命令附加到prompt中（--wm）
   */
  watermark?: boolean;
  /**
   * 是否固定摄像机，可选参数。作为文本命令附加到prompt中（--cf）
   */
  camerafixed?: boolean;
  /**
   * 是否返回最后一帧图像，可选参数
   */
  return_last_frame?: boolean;
}

export interface VolcesSeedance30I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedance_30_i2v
 *
 * Volces SeeDance 3.0 I2V Video Generator
 * 
 * Image-to-video generation using OpenAI protocol. Converts images to videos with optional text prompts.
 * Model: doubao-seedance-1-0-lite-i2v
 * Duration: 3-12 seconds
 * Resolution: 480p, 720p, 1080p
 * 
 * API Documentation: https://www.volcengine.com/docs/82379/1520757
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedance30I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedance30I2v({
 *   // 参数见 VolcesSeedance30I2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedance30I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedance30I2v(
  params: VolcesSeedance30I2vParams,
): Promise<VolcesSeedance30I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedance_30_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedance30I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
